import { useEffect, useState } from 'react'
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Save, AlertCircle, Mail } from 'lucide-react'

interface SpecialMessage {
  body: string
  createdAt?: Timestamp
}

export default function SpecialMessageTab() {
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [messageStatus, setMessageStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    loadMessage()
  }, [])

  const loadMessage = async () => {
    try {
      const docRef = doc(db, 'specialMessages', 'current')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data() as SpecialMessage
        setMessage(data.body || '')
      } else {
        // Initialize with empty message
        setMessage('')
      }
    } catch (error) {
      console.error('Error loading special message:', error)
      setMessageStatus({ type: 'error', text: 'Mesaj yüklenirken hata oluştu' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!message.trim()) {
      setMessageStatus({ type: 'error', text: 'Mesaj boş olamaz' })
      return
    }

    setSaving(true)
    setMessageStatus(null)

    try {
      const docRef = doc(db, 'specialMessages', 'current')
      await setDoc(docRef, {
        body: message.trim(),
        createdAt: Timestamp.now(),
      }, { merge: true })
      
      setMessageStatus({ type: 'success', text: 'Özel mesaj başarıyla kaydedildi' })
    } catch (error) {
      console.error('Error saving special message:', error)
      setMessageStatus({ type: 'error', text: 'Mesaj kaydedilirken hata oluştu' })
    } finally {
      setSaving(false)
    }
  }

  const handleClear = () => {
    if (confirm('Mesajı temizlemek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      setMessage('')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const charCount = message.length
  const maxLength = 5000 // Reasonable limit for special messages

  return (
    <div className="space-y-6">
      {messageStatus && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            messageStatus.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <AlertCircle className="w-5 h-5" />
          <span>{messageStatus.text}</span>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Özel Mesaj Yönetimi</h3>
            <p className="text-sm text-blue-700">
              Bu mesaj <strong>/special</strong> sayfasına gelen tüm kullanıcılara gösterilir. 
              Mesajı güncellediğinizde, linki açan herkes yeni içeriği görecektir.
            </p>
          </div>
        </div>
      </div>

      {/* Message Editor */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Mesaj İçeriği</h3>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${
              charCount > maxLength ? 'text-red-600' : 'text-gray-500'
            }`}>
              {charCount} / {maxLength} karakter
            </span>
          </div>
        </div>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Özel mesajınızı buraya yazın...&#10;&#10;Bu mesaj /special sayfasında gösterilecektir."
          className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none font-body text-gray-900"
          maxLength={maxLength}
        />

        {charCount > maxLength && (
          <p className="text-sm text-red-600 mt-2">
            Mesaj çok uzun. Lütfen {maxLength} karakterden az olacak şekilde düzenleyin.
          </p>
        )}

        {/* Preview */}
        {message.trim() && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Önizleme:</h4>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap font-body">
                {message}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleClear}
          disabled={!message.trim() || saving}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Temizle
        </button>
        <button
          onClick={handleSave}
          disabled={!message.trim() || saving || charCount > maxLength}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>

      {/* Link Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Kullanım:</h4>
        <p className="text-sm text-gray-600 mb-2">
          Bu mesajı görmek için kullanıcılar şu linke gitmelidir:
        </p>
        <code className="block p-2 bg-white border border-gray-300 rounded text-sm text-gray-900 font-mono break-all">
          {window.location.origin}/special
        </code>
      </div>
    </div>
  )
}
